export const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? 0);
export const pct = (v, digits = 1) => (typeof v === "number" ? `${v.toFixed(digits)}%` : "0%");
export const round1 = (v) => (typeof v === "number" ? Math.round(v * 10) / 10 : 0);
export const round2 = (v) => (typeof v === "number" ? Math.round(v * 100) / 100 : 0);

export const fmtLakhs = (v) => {
  if (typeof v !== "number") return "—";
  if (v >= 100_000) return `${(v / 100_000).toFixed(2).replace(/\.?0+$/, "")}L`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return fmt(v);
};

/** Executive readiness: entry + school closure weighted; certification is the main drag. */
export const computeExecutiveReadiness = (overview) => {
  if (!overview) return 0;
  const completion = overview.completion_rate ?? 0;
  const fullSchools = overview.full_completion_rate ?? 0;
  const certification = overview.certification_rate ?? 0;
  return round1(completion * 0.45 + fullSchools * 0.35 + certification * 0.2);
};

export const buildReadinessGaugeStyle = (score) => {
  const color =
    score >= 90 ? "var(--de-success)" : score >= 75 ? "var(--de-warning)" : "var(--de-warning)";
  const track = score >= 90 ? "rgba(16,185,129,.14)" : "rgba(245,158,11,.14)";
  return {
    background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(${color} 0 ${Math.min(score, 100)}%, ${track} ${Math.min(score, 100)}% 100%)`,
  };
};

export const buildCompletionDonutStyle = (completionPct, inProgressPct, pendingPct) => {
  const complete = Math.min(completionPct, 100);
  const inProg = Math.min(complete + inProgressPct, 100);
  return {
    background: `radial-gradient(circle at center, #fff 0 54%, transparent 55%), conic-gradient(var(--de-success) 0 ${complete}%, var(--de-warning) ${complete}% ${inProg}%, var(--de-danger) ${inProg}% 100%)`,
  };
};

export const buildRingStyle = (value, tone = "green") => {
  const colors = {
    green: "var(--de-success)",
    orange: "var(--de-secondary)",
    red: "var(--de-danger)",
    blue: "var(--de-primary)",
  };
  const color = colors[tone] ?? colors.green;
  const score = Math.min(value, 100);
  return {
    background: `radial-gradient(circle at center, white 0 58%, transparent 59%), conic-gradient(${color} 0 ${score}%, rgba(37,99,235,.10) ${score}% 100%)`,
  };
};

export const buildHeroNarrative = (overview) => {
  if (!overview) {
    return "No data entry status records are available for the selected scope.";
  }
  const certified = overview.certified_schools ?? 0;
  const totalSchools = overview.total_schools ?? 0;
  const completion = overview.completion_rate ?? 0;
  const certRate = overview.certification_rate ?? 0;

  if (certRate < 25) {
    return `Student records are ${completion >= 99 ? "almost fully entered" : "largely entered"}, but the executive risk sits in school certification. Only ${fmt(certified)} of ${fmt(totalSchools)} schools are certified, so completion must now move into verified closure.`;
  }
  if (completion < 95) {
    return `Data entry completion is at ${pct(completion, 2)} with ${fmt(overview.pending_students)} pending student records. Block officers should close entry gaps before pushing certification.`;
  }
  return `Entry completion is strong at ${pct(completion, 2)} and ${pct(overview.full_completion_rate ?? 0, 1)} of schools are fully complete. Focus executive attention on certifying the remaining ${fmt(overview.non_certified_schools)} schools.`;
};

export const buildPrimaryAction = (overview) => {
  if (!overview) return "Import or refresh data entry status records for the selected scope.";
  const nonCert = overview.non_certified_schools ?? 0;
  const pending = overview.pending_students ?? 0;
  if (nonCert > 0 && (overview.certification_rate ?? 0) < 50) {
    return `Shift officer focus from data entry completion to certification closure for ${fmt(nonCert)} non-certified schools.`;
  }
  if (pending > 0) {
    return `Clear ${fmt(pending)} pending student records across blocks before the next review cycle.`;
  }
  return "Maintain certification cadence and audit repeater flags for residual exceptions.";
};

export const getTopBlockSubtitle = (block, rank) => {
  const rate = block.completion_rate ?? 0;
  const pending = block.pending_students ?? 0;
  if (rate >= 99.99) {
    const labels = [
      "Low pending load · strong closure",
      "High consistency across schools",
      "Near complete student entry",
      "Stable data operations",
      "Ready for certification push",
    ];
    return labels[rank] ?? "Strong completion consistency";
  }
  if (pending === 0) return "Zero pending student records";
  return `${fmt(pending)} pending · ${pct(rate, 2)} complete`;
};

export const getAttentionBlockSubtitle = (block) => {
  const rate = block.completion_rate ?? 0;
  const cert = block.certification_rate ?? 0;
  const pending = block.pending_students ?? 0;
  if (rate < 99.5) return "Highest remaining data gap";
  if (pending > 50) return "Largest pending student backlog";
  if (cert < 8) return "Certification and pending review needed";
  if ((block.total_schools ?? 0) > 400) return "Urban school volume requires audit sampling";
  return "Track repeaters and final records";
};

export const getBlockPriority = (block) => {
  const completion = block.completion_pct ?? block.completion_rate ?? 0;
  const pending = block.pending_students ?? 0;
  const cert = block.certification_rate ?? 0;
  if (completion < 99.7 || pending >= 80 || cert < 6) return { label: "High", tone: "red" };
  if (completion < 99.95 || pending >= 40 || cert < 8) return { label: "Medium", tone: "" };
  return { label: "Low", tone: "green" };
};

export const getBlockNextAction = (block) => {
  const completion = block.completion_pct ?? block.completion_rate ?? 0;
  const pending = block.pending_students ?? 0;
  const cert = block.certification_rate ?? 0;
  const repeaters = block.repeater_rate ?? 0;

  if (completion < 99.7 || pending >= 80) return "Close pending data and certify cluster schools";
  if (cert < 7) return "Officer certification camp";
  if (pending >= 40) return "Validate school declarations";
  if ((block.total_schools ?? 0) > 400) return "Sample audit high-volume schools";
  if (repeaters > 1.5) return "Audit repeater flags and duplicates";
  return "Final closure review";
};

export const buildAttentionBlocks = (blockData, limit = 5) => {
  return [...(blockData ?? [])]
    .sort((a, b) => (a.completion_pct ?? 0) - (b.completion_pct ?? 0))
    .slice(0, limit)
    .map((block) => ({
      ...block,
      full_completion_rate: block.total_schools
        ? round1(((block.full_completion_schools ?? 0) / block.total_schools) * 100)
        : 0,
      priority: getBlockPriority(block),
      next_action: getBlockNextAction(block),
      subtitle: getAttentionBlockSubtitle({
        ...block,
        completion_rate: block.completion_pct,
        certification_rate: block.certification_rate,
      }),
    }));
};

export const buildActionQueue = (overview, attentionBlocks) => {
  const blockNames = attentionBlocks.slice(0, 4).map((b) => b.block_name).join(", ");
  return [
    {
      tone: "red",
      title: "Certify Non-Certified Schools",
      body: `${fmt(overview?.non_certified_schools ?? 0)} schools need officer certification even though most records are entered.`,
      footer: "Owner: Block Education Officer",
    },
    {
      tone: "orange",
      title: "Clear Pending Student Records",
      body: blockNames
        ? `Focus on ${blockNames} before the next weekly review.`
        : `Clear ${fmt(overview?.pending_students ?? 0)} pending records across priority blocks.`,
      footer: "Target: 100% entry closure",
    },
    {
      tone: "purple",
      title: "Validate Repeater Records",
      body: `${fmt(overview?.total_repeaters ?? 0)} repeater records should be sampled and validated for duplicate or stale status.`,
      footer: "Target: reduce exception backlog",
    },
  ];
};

export const buildClosurePlan = (attentionBlocks) => {
  const topBlocks = attentionBlocks
    .slice(0, 5)
    .map((b) => b.block_name)
    .filter(Boolean)
    .join(", ");
  return [
    {
      week: "Week 1",
      text: "Publish block-wise pending student and non-certified school list to all BEOs.",
    },
    {
      week: "Week 2",
      text: topBlocks
        ? `Run certification camps for ${topBlocks}.`
        : "Run certification camps for top pending and low-certification blocks.",
    },
    {
      week: "Week 3",
      text: "Audit repeater flags, duplicate records and inconsistent school declarations.",
    },
    {
      week: "Week 4",
      text: "Lock certified schools and escalate only residual exceptions to district review.",
    },
  ];
};

export const maxBandWidth = (bands) => {
  const values = Object.values(bands ?? {}).filter((v) => typeof v === "number");
  return Math.max(...values, 1);
};

export const buildCompletionGaugeStyle = (completionPct) => {
  const score = Math.min(completionPct, 100);
  return {
    background: `radial-gradient(circle at center, #fff 0 52%, transparent 53%), conic-gradient(var(--de-success) 0 ${score}%, rgba(16,185,129,.13) ${score}% 100%)`,
  };
};

export const buildBlockPerformanceNarrative = (overview) => {
  const blocks = overview?.total_blocks ?? 0;
  const completion = overview?.completion_rate ?? 0;
  if (completion >= 99) {
    return `Student data entry is almost complete across all ${fmt(blocks)} blocks. The operating risk has shifted from entry completion to certification, repeaters and targeted pending closures.`;
  }
  return `Data entry completion is at ${pct(completion, 2)} across ${fmt(blocks)} blocks. Officers should close remaining pending records before scaling certification drives.`;
};

export const buildBlockPerformanceFocus = (topRepeaterBlocks, topPendingBlocks) => {
  const repeaterNames = topRepeaterBlocks.slice(0, 5).map((b) => b.block_name).join(", ");
  if (repeaterNames) {
    return `Move from mass data entry follow-up to block-level certification drives and repeater validation for ${repeaterNames}.`;
  }
  const pendingNames = topPendingBlocks.slice(0, 5).map((b) => b.block_name).join(", ");
  return pendingNames
    ? `Prioritize pending student closure for ${pendingNames} before the next district review.`
    : "Maintain certification cadence and sample repeater records in outlier blocks.";
};

export const getTopPendingBlocks = (blockData, limit = 5) =>
  [...(blockData ?? [])].sort((a, b) => (b.pending_students ?? 0) - (a.pending_students ?? 0)).slice(0, limit);

export const getTopRepeaterBlocks = (blockData, limit = 5) =>
  [...(blockData ?? [])].sort((a, b) => (b.total_repeaters ?? 0) - (a.total_repeaters ?? 0)).slice(0, limit);

export const getHighestPendingBlockName = (blockData) => getTopPendingBlocks(blockData, 1)[0]?.block_name ?? "—";

export const getOfficerPriority = (block) => {
  const pending = block.pending_students ?? 0;
  const cert = block.certification_rate ?? 0;
  const repeaters = block.total_repeaters ?? 0;
  const completion = block.completion_pct ?? block.completion_rate ?? 0;

  if (pending >= 80) return { label: "Close Pending", tone: "bad" };
  if (cert < 5 && completion >= 99.5) return { label: "Certification Drive", tone: "bad" };
  if (cert < 8 && completion < 99.7) return { label: "Low Certification", tone: "bad" };
  if (repeaters >= 3000) return { label: "High Repeater", tone: "bad" };
  if (repeaters >= 2500 && cert < 15) return { label: "Certify + Repeaters", tone: "bad" };
  if (repeaters >= 2000 && (block.repeater_rate ?? 0) >= 1.2) return { label: "Repeater Spike", tone: "bad" };
  if (repeaters >= 1500) return { label: "Repeater Check", tone: "warn" };
  if (cert < 20) return { label: "Certify", tone: "warn" };
  return { label: "Monitor", tone: "" };
};

export const isRiskRank = (block) => {
  const priority = getOfficerPriority(block);
  return priority.tone === "bad" || (block.pending_students ?? 0) >= 50 || (block.completion_pct ?? 0) < 99.95;
};

export const getCompletionBadge = (value) => {
  if (value >= 99.95) return { label: pct(value, 2), tone: "" };
  if (value >= 99.5) return { label: pct(value, 2), tone: "warn" };
  return { label: pct(value, 2), tone: "bad" };
};

export const getPendingBadge = (pending) => {
  if (pending >= 80) return { label: fmt(pending), tone: "bad" };
  if (pending > 0) return { label: fmt(pending), tone: "warn" };
  return { label: fmt(pending), tone: "" };
};

export const getCertificationBadge = (value) => {
  if (value >= 15) return { label: pct(value, 1), tone: "" };
  if (value >= 8) return { label: pct(value, 1), tone: "warn" };
  return { label: pct(value, 1), tone: "bad" };
};

export const buildBlockPerformanceActionQueue = (overview, topRepeaterBlocks, topPendingBlocks) => {
  const certRate = overview?.certification_rate ?? 0;
  const repeaterNames = topRepeaterBlocks.slice(0, 5).map((b) => b.block_name).join(", ");
  const pendingNames = topPendingBlocks.slice(0, 5).map((b) => b.block_name).join(", ");
  const certTarget = Math.min(40, Math.round(certRate + 32));

  return [
    {
      tone: "red",
      title: "Run Certification War-Room",
      body: `Move certified schools from ${pct(certRate, 1)} to ${certTarget}% by assigning daily block-level certification targets.`,
    },
    {
      tone: "orange",
      title: "Validate Top Repeater Blocks",
      body: repeaterNames
        ? `Audit ${repeaterNames} before certification sign-off.`
        : "Audit high repeater blocks before certification sign-off.",
    },
    {
      tone: "purple",
      title: "Close Pending Micro-Load",
      body: pendingNames
        ? `Resolve ${pendingNames} pending cases in the next review cycle.`
        : `Resolve ${fmt(overview?.pending_students ?? 0)} pending student records in the next review cycle.`,
    },
  ];
};

export const buildBlockPerformanceClosurePlan = (overview) => {
  const pending = overview?.pending_students ?? 0;
  return [
    {
      week: "Week 1",
      title: "Freeze Remaining Pending List",
      text: `Generate block-wise school lists for all ${fmt(pending)} pending students and allocate owner names.`,
    },
    {
      week: "Week 2",
      title: "Complete Repeater Sampling",
      text: "Validate high repeater blocks through headmaster confirmation and automated duplicate checks.",
    },
    {
      week: "Week 3",
      title: "Certification Sprint",
      text: "Prioritize blocks with completion above 99.95% but certification below 15%.",
    },
    {
      week: "Week 4",
      title: "District Review and Exception Closure",
      text: "Review non-certified schools, pending exceptions and outlier blocks in one officer meeting.",
    },
  ];
};

export const bandWidthPct = (value, total) => (total > 0 ? Math.max((value / total) * 100, value > 0 ? 0.4 : 0) : 0);

export const barWidthPct = (value, max) => (max > 0 ? Math.max((value / max) * 100, value > 0 ? 20 : 0) : 0);

export const fmtShortK = (v) => {
  if (typeof v !== "number") return "—";
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return fmt(v);
};

export const computeSchoolMonitoringReadiness = (overview, criticalCount) => {
  if (!overview) return 0;
  const completion = overview.completion_rate ?? 0;
  const fullSchools = overview.full_completion_rate ?? 0;
  const certification = overview.certification_rate ?? 0;
  const totalSchools = overview.total_schools ?? 1;
  const criticalPenalty = Math.min(12, (criticalCount / totalSchools) * 100 * 1.5);
  return round1(
    completion * 0.45 + fullSchools * 0.32 + certification * 0.2 + Math.max(0, 10 - criticalPenalty)
  );
};

export const buildSchoolMonitoringGaugeStyle = (score) => ({
  background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(var(--de-warning) 0 ${Math.min(score, 100)}%, rgba(245,158,11,.14) ${Math.min(score, 100)}% 100%)`,
});

export const buildCertificationDonutStyle = (certRate) => {
  const certified = Math.min(certRate, 100);
  return {
    background: `radial-gradient(circle at center, white 0 53%, transparent 54%), conic-gradient(var(--de-success) 0 ${certified}%, var(--de-danger) ${certified}% 100%)`,
  };
};

export const getSchoolOpenCount = (school) => (school.in_progress ?? 0) + (school.not_started ?? 0);

export const getSchoolRiskLabel = (completionPct) => {
  if (completionPct < 90) return { label: "Critical", tone: "" };
  if (completionPct < 95) return { label: "High", tone: "amber" };
  return { label: "Watch", tone: "amber" };
};

export const getSchoolStatusTag = (completionPct) => {
  if (completionPct < 90) return { label: "Critical", tone: "red" };
  if (completionPct < 95) return { label: "High", tone: "" };
  return { label: "Watch", tone: "" };
};

export const getRepeaterAction = (rate) =>
  rate >= 95 ? { label: "Verify", tone: "red" } : { label: "Audit", tone: "" };

export const buildCriticalSchoolSubtitle = (school) => {
  const parts = [`${pct(school.completion_pct ?? 0, 2)} completion`];
  if ((school.in_progress ?? 0) > 0) parts.push(`${school.in_progress} in progress`);
  if ((school.not_started ?? 0) > 0) parts.push(`${school.not_started} not started`);
  return `${titleCase(school.block_name)} · ${parts.join(" · ")}`;
};

export const computeCriticalSchoolCount = (completionBands, criticalSchools) => {
  if (completionBands) {
    const fromBands = (completionBands["90-95%"] ?? 0) + (completionBands["<90%"] ?? 0);
    if (fromBands > 0) return fromBands;
  }
  return criticalSchools?.length ?? 0;
};

export const getEntryStabilityChip = (overview) => {
  const completion = overview?.completion_rate ?? 0;
  const pending = overview?.pending_students ?? 0;
  if (completion >= 99.95 && pending < 100) return { label: "Entry Stable", tone: "green" };
  if (completion >= 99) return { label: "Entry Near Complete", tone: "green" };
  if (completion >= 95) return { label: "Entry in Progress", tone: "" };
  return { label: "Entry at Risk", tone: "red" };
};

export const getSnapshotRiskLabel = (metric, value, overview) => {
  switch (metric) {
    case "completion":
      return value >= 99.5 ? "Green" : value >= 95 ? "Watch" : "Critical";
    case "certification":
      return value >= 25 ? "Moderate" : value >= 10 ? "Watch" : "Critical";
    case "repeater":
      return value <= 1 ? "Low" : value <= 2 ? "Review" : "High";
    case "pending": {
      const pending = overview?.pending_students ?? 0;
      return pending <= 50 ? "Low" : pending <= 200 ? "Closeout" : "Urgent";
    }
    default:
      return "Review";
  }
};

export const buildSchoolMonitoringNarrative = (overview, criticalCount, highRepeaterCount) => {
  if (!overview) return "No school monitoring data available for the selected scope.";
  const completion = overview.completion_rate ?? 0;
  const nonCert = overview.non_certified_schools ?? 0;
  return `District completion is ${pct(completion, 2)} across ${fmt(overview.total_students ?? 0)} students, with ${fmt(criticalCount)} schools below 95%, ${fmt(highRepeaterCount)} high-repeater schools, and ${fmt(nonCert)} schools awaiting certification.`;
};

export const buildSchoolMonitoringPrimaryAction = (overview, criticalCount, certRate) => {
  if (!overview) return "Refresh data for the selected scope.";
  if (criticalCount > 0) {
    return `Close ${fmt(criticalCount)} schools below 95% first (${fmt(overview.pending_students ?? 0)} pending students district-wide), then certify ${fmt(overview.full_completion_schools ?? 0)} full-entry schools where certification is only ${pct(certRate, 1)}.`;
  }
  if ((overview.certification_rate ?? 0) < 25) {
    return `Shift to certification closure: ${fmt(overview.non_certified_schools ?? 0)} non-certified schools need officer sign-off.`;
  }
  return `Validate ${fmt(overview.total_repeaters ?? 0)} repeater records and maintain certification cadence.`;
};

export const buildSchoolMonitoringInsightText = (type, overview, criticalCount, highRepeaterCount, certRate) => {
  if (!overview) return "";
  switch (type) {
    case "critical":
      return `${fmt(criticalCount)} schools are below 95% completion with ${fmt(overview.pending_students ?? 0)} pending students still open district-wide.`;
    case "repeater":
      return `${fmt(overview.total_repeaters ?? 0)} repeaters (${pct(overview.repeater_rate ?? 0, 2)}) across ${fmt(highRepeaterCount)} schools above 5% repeater rate need validation.`;
    case "cert":
      return `${fmt(overview.non_certified_schools ?? 0)} schools (${pct(100 - certRate, 1)}) are not certified despite ${pct(overview.full_completion_rate ?? 0, 1)} full-entry schools.`;
    case "completion":
      return `${pct(overview.completion_rate ?? 0, 2)} completion with ${fmt(overview.total_completed ?? 0)} records entered enables audit closure focus.`;
    default:
      return "";
  }
};

export const buildSchoolMonitoringActionQueue = (overview, criticalCount, highRepeaterCount) => [
  {
    tone: "red",
    title: "Close Critical School Entries",
    body: `Assign block officers to the ${fmt(criticalCount)} schools below 95% and track daily completion.`,
    tag: "Day 1–7",
    tagTone: "red",
  },
  {
    tone: "orange",
    title: "Audit High Repeater Schools",
    body: `Validate repeater records in ${fmt(highRepeaterCount)} schools above 5% repeater rate (${fmt(overview?.total_repeaters ?? 0)} total repeaters).`,
    tag: "Day 8–15",
    tagTone: "",
  },
  {
    tone: "green",
    title: "Start Certification Sprint",
    body: `Move ${fmt(overview?.full_completion_schools ?? 0)} full-entry schools into certification batches; only ${fmt(overview?.certified_schools ?? 0)} of ${fmt(overview?.total_schools ?? 0)} are certified.`,
    tag: "Day 16–30",
    tagTone: "green",
  },
];

export const buildSchoolMonitoringClosurePlan = (overview, criticalCount, highRepeaterCount) => {
  const pending = overview?.pending_students ?? 0;
  const fullEntry = overview?.full_completion_schools ?? 0;
  const nonCert = overview?.non_certified_schools ?? 0;
  return [
    {
      week: "Week 1",
      title: "Exception Clean-Up",
      text: `Complete pending entries in ${fmt(criticalCount)} critical schools and clear ${fmt(pending)} pending student records.`,
    },
    {
      week: "Week 2",
      title: "Repeater Validation",
      text: `Audit ${fmt(highRepeaterCount)} schools with >5% repeaters (${fmt(overview?.total_repeaters ?? 0)} total) and classify valid, duplicate, or correction cases.`,
    },
    {
      week: "Week 3",
      title: "Certification Wave 1",
      text: `Prioritize ${fmt(fullEntry)} full-entry schools and assign daily certification targets for ${fmt(nonCert)} non-certified schools.`,
    },
    {
      week: "Week 4",
      title: "Governance Sign-Off",
      text: `Publish certified (${fmt(overview?.certified_schools ?? 0)}) vs non-certified (${fmt(nonCert)}) list with evidence status and officer ownership.`,
    },
  ];
};

export const titleCase = (value) =>
  (value || "")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const computeAuditTrustScore = (overview, dataQuality) => {
  if (!overview) return 0;
  const consistency = dataQuality?.consistency_rate ?? 0;
  const completion = overview.completion_rate ?? 0;
  const certification = overview.certification_rate ?? 0;
  let score = consistency * 0.45 + completion * 0.42 + certification * 0.08 + 4;
  if ((dataQuality?.mismatch_schools ?? 1) === 0) score += 0.5;
  if ((dataQuality?.zero_entry_schools ?? 1) === 0) score += 0.3;
  return round1(Math.min(100, score));
};

export const buildAuditTrustGaugeStyle = (score) => ({
  background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(var(--de-accent) 0 ${Math.min(score, 100)}%, rgba(20,184,166,.14) ${Math.min(score, 100)}% 100%)`,
  boxShadow: "inset 0 0 0 1px var(--de-border), 0 20px 42px rgba(20,184,166,.13)",
});

export const buildQualityCertDonutStyle = (certRate) => {
  const certified = Math.min(certRate, 100);
  return {
    background: `radial-gradient(circle at center, white 0 55%, transparent 56%), conic-gradient(var(--de-success) 0 ${certified}%, var(--de-danger) ${certified}% 100%)`,
    boxShadow: "inset 0 0 0 1px var(--de-border), 0 18px 36px rgba(239,68,68,.10)",
  };
};

export const funnelWidthPct = (value, max) => (max > 0 ? Math.max((value / max) * 100, value > 0 ? 4 : 0) : 0);

export const getCertificationCoverageBlocks = (blockData, limit = 6) =>
  [...(blockData ?? [])]
    .sort((a, b) => (b.certification_rate ?? 0) - (a.certification_rate ?? 0))
    .slice(0, limit);

const getBlockCompletion = (block) => block.completion_pct ?? block.completion_rate ?? 0;

export const getBlockAuditRiskSignal = (block, lowestCompletionBlock) => {
  const completion = getBlockCompletion(block);
  const cert = block.certification_rate ?? 0;
  const repeaters = block.total_repeaters ?? 0;

  if (block.block_name === lowestCompletionBlock?.block_name) {
    return { label: "Lowest Completion", tone: "red" };
  }
  if (cert < 8 && repeaters >= 1000) return { label: "Certification Gap", tone: "orange" };
  if (cert < 10) return { label: "Below 10%", tone: "orange" };
  if (repeaters >= 3000) return { label: "High Repeaters", tone: "red" };
  if (cert < 25) return { label: "Improve Sign-Off", tone: "blue" };
  return { label: "Monitor", tone: "blue" };
};

export const getBlockAuditPriority = (block, lowestCompletionBlock) => {
  const completion = getBlockCompletion(block);
  const cert = block.certification_rate ?? 0;
  const repeaters = block.total_repeaters ?? 0;

  if (block.block_name === lowestCompletionBlock?.block_name || completion < 99.7) {
    return { label: "P1", tone: "red" };
  }
  if (cert < 8 || repeaters >= 3000) return { label: "P1", tone: "red" };
  if (cert < 10 || repeaters >= 1500) return { label: "P2", tone: "orange" };
  return { label: "P3", tone: "blue" };
};

export const getBlockAuditAction = (risk) => {
  const map = {
    "Lowest completion": "Escalate",
    "Certification gap": "Assign BEO",
    "Below 10%": "Verify",
    "High repeaters": "Sample audit",
    "Improve sign-off": "Monitor",
    Monitor: "Monitor",
  };
  return map[risk.label] ?? "Review";
};

export const buildBlockAuditRows = (blockData) => {
  const blocks = [...(blockData ?? [])];
  if (!blocks.length) return [];

  const lowestCompletionBlock = [...blocks].sort(
    (a, b) => getBlockCompletion(a) - getBlockCompletion(b)
  )[0];

  const scored = blocks.map((block) => {
    const completion = getBlockCompletion(block);
    const cert = block.certification_rate ?? 0;
    const repeaters = block.total_repeaters ?? 0;
    let score = (100 - completion) * 20;
    score += cert < 10 ? (10 - cert) * 8 : 0;
    score += repeaters / 100;
    if (block.block_name === lowestCompletionBlock.block_name) score += 50;
    return { block, score };
  });

  return scored
    .sort((a, b) => b.score - a.score)
    .slice(0, 5)
    .map(({ block }) => {
      const risk = getBlockAuditRiskSignal(block, lowestCompletionBlock);
      return {
        ...block,
        priority: getBlockAuditPriority(block, lowestCompletionBlock),
        risk,
        action: getBlockAuditAction(risk),
      };
    });
};

export const buildQualityAuditNarrative = (overview, dataQuality, certRate, nonCertSchools) => {
  const consistency = dataQuality?.consistency_rate ?? 0;
  const completion = overview?.completion_rate ?? 0;
  return `Data consistency is ${pct(consistency, 1)} with ${pct(completion, 2)} completion, but only ${pct(certRate, 1)} of schools are certified. ${fmt(nonCertSchools)} schools still need audit-ready sign-off.`;
};

export const buildQualityAuditPrimaryAction = (overview, certCoverageBlocks) => {
  const lowCertBlocks = certCoverageBlocks
    .filter((b) => (b.certification_rate ?? 0) < 10)
    .slice(0, 3)
    .map((b) => titleCase(b.block_name))
    .join(", ");
  const nonCert = overview?.non_certified_schools ?? 0;
  if (lowCertBlocks) {
    return `Assign block-wise certification targets for ${fmt(nonCert)} non-certified schools and escalate ${lowCertBlocks} where certification is below 10%.`;
  }
  return `Move from completion monitoring to certification closure for ${fmt(nonCert)} non-certified schools.`;
};

export const buildQualityMetricDescription = (field, dataQuality, overview) => {
  switch (field) {
    case "consistency":
      return `${fmt(dataQuality?.consistent_schools ?? 0)} of ${fmt(overview?.total_schools ?? 0)} schools passed structural quality checks.`;
    case "mismatch": {
      const mismatches = dataQuality?.mismatch_schools ?? 0;
      return mismatches === 0
        ? "No schools currently showing mismatch issues."
        : `${fmt(mismatches)} schools have data mismatch flags requiring review.`;
    }
    case "zero_entry": {
      const zero = dataQuality?.zero_entry_schools ?? 0;
      return zero === 0
        ? "No school left fully unentered in the system."
        : `${fmt(zero)} schools have zero entry records and need immediate follow-up.`;
    }
    case "full_entry":
      return `${fmt(dataQuality?.full_entry_schools ?? overview?.full_completion_schools ?? 0)} schools at 100% completion are ready for verification.`;
    default:
      return "";
  }
};

export const buildQualityAuditActionQueue = (overview, certificationData, dataQuality, blockData) => {
  const nonCert = certificationData?.non_certified?.schools ?? overview?.non_certified_schools ?? 0;
  const certRate = certificationData?.certification_rate ?? overview?.certification_rate ?? 0;
  const repeaters = overview?.total_repeaters ?? 0;
  const mismatches = dataQuality?.mismatch_schools ?? 0;
  const lowCertBlock = [...(blockData ?? [])].sort(
    (a, b) => (a.certification_rate ?? 0) - (b.certification_rate ?? 0)
  )[0]?.block_name;

  return [
    {
      tone: "red",
      title: "Close Certification Backlog",
      body: `Assign daily certification targets to certify ${fmt(nonCert)} non-certified schools (current rate ${pct(certRate, 1)}).`,
      due: "7 days",
    },
    {
      tone: "orange",
      title: "Validate High Repeater Schools",
      body: `Run sample audit on ${fmt(repeaters)} repeater records (${pct(overview?.repeater_rate ?? 0, 2)} district rate)${lowCertBlock ? ` starting with ${titleCase(lowCertBlock)}` : ""}.`,
      due: "10 days",
    },
    {
      tone: "green",
      title: "Protect Current Data Consistency",
      body:
        mismatches === 0
          ? `Maintain ${pct(dataQuality?.consistency_rate ?? 0, 1)} consistency with ${fmt(dataQuality?.zero_entry_schools ?? 0)} zero-entry schools through daily validation.`
          : `Resolve ${fmt(mismatches)} mismatch schools and restore ${pct(dataQuality?.consistency_rate ?? 0, 1)} consistency baseline.`,
      due: "Daily",
    },
  ];
};

export const parseInsightBullets = (markdown) =>
  String(markdown || "")
    .split("\n")
    .map((line) => line.replace(/^[-*]\s+/, "").replace(/\*\*/g, "").trim())
    .filter((line) => line && !line.startsWith("#"));

export const buildDataEntryExecutiveSummary = (overview, criticalCount) => {
  if (!overview) return [];
  return [
    `Completion rate: ${pct(overview.completion_rate ?? 0, 2)} across ${fmt(overview.total_students ?? 0)} students.`,
    `Pending students: ${fmt(overview.pending_students ?? 0)} (${pct(overview.pending_rate ?? 0, 2)}).`,
    `Certification rate: ${pct(overview.certification_rate ?? 0, 1)} with ${fmt(overview.certified_schools ?? 0)} schools certified.`,
    `Critical schools (<95% completion): ${fmt(criticalCount)}.`,
    `Full completion schools: ${fmt(overview.full_completion_schools ?? 0)} (${pct(overview.full_completion_rate ?? 0, 1)}).`,
    `Repeater rate: ${pct(overview.repeater_rate ?? 0, 2)} (${fmt(overview.total_repeaters ?? 0)} students).`,
  ];
};

export const buildDataEntryWatchlist = (overview, criticalCount) => [
  {
    rank: 1,
    label: "Low Completion Schools",
    value: fmt(criticalCount),
    tone: "red",
    badgeTone: "red",
  },
  {
    rank: 2,
    label: "Certification Backlog",
    value: `${fmt(overview?.non_certified_schools ?? 0)} non-certified schools`,
    tone: "orange",
    badgeTone: "orange",
  },
  {
    rank: 3,
    label: "Repeater Cohort",
    value: `${fmt(overview?.total_repeaters ?? 0)} students`,
    tone: "blue",
    badgeTone: "",
  },
  {
    rank: 4,
    label: "Pending Load",
    value: `${fmt(overview?.pending_students ?? 0)} students`,
    tone: "green",
    badgeTone: "green",
  },
];

export const buildDataEntryDefaultSignals = (overview, criticalCount, blockData) => {
  if (!overview) return ["No data entry records available for the selected scope."];
  const signals = [];
  const certRate = overview.certification_rate ?? 0;
  const nonCert = overview.non_certified_schools ?? 0;
  const pending = overview.pending_students ?? 0;
  const repeaters = overview.total_repeaters ?? 0;

  if (certRate < 25) {
    signals.push(
      `Low certification coverage at ${pct(certRate, 1)} with ${fmt(nonCert)} non-certified schools across the district.`
    );
  }
  if (criticalCount > 0) {
    signals.push(`${fmt(criticalCount)} schools remain below 95% completion and need immediate closure.`);
  }
  const topRepeaterBlock = getTopRepeaterBlocks(blockData, 1)[0];
  if (repeaters > 0) {
    signals.push(
      topRepeaterBlock
        ? `${fmt(repeaters)} repeaters (${pct(overview.repeater_rate ?? 0, 2)}) concentrated in blocks such as ${titleCase(topRepeaterBlock.block_name)}.`
        : `${fmt(repeaters)} repeater records (${pct(overview.repeater_rate ?? 0, 2)}) require validation across the district.`
    );
  }
  if (pending > 0) {
    const topPending = getHighestPendingBlockName(blockData);
    signals.push(
      topPending !== "—"
        ? `${fmt(pending)} pending student entries remain, led by ${titleCase(topPending)} block.`
        : `${fmt(pending)} pending student entries still require closure.`
    );
  }
  if (!signals.length) {
    signals.push(`Entry completion is ${pct(overview.completion_rate ?? 0, 2)} with certification at ${pct(certRate, 1)}.`);
  }
  return signals.slice(0, 4);
};

export const buildDataEntryDefaultRecommendations = (overview, criticalCount, blockData) => {
  if (!overview) return ["Refresh data or broaden filters for the selected scope."];
  const recs = [];
  const nonCert = overview.non_certified_schools ?? 0;
  const pending = overview.pending_students ?? 0;
  const topPendingBlocks = getTopPendingBlocks(blockData, 3).map((b) => titleCase(b.block_name));

  if (nonCert > 0) {
    recs.push(`Prioritize certification drives for ${fmt(nonCert)} non-certified schools (current rate ${pct(overview.certification_rate ?? 0, 1)}).`);
  }
  if (pending > 0) {
    recs.push(
      topPendingBlocks.length
        ? `Close ${fmt(pending)} pending entries in ${topPendingBlocks.join(", ")} with daily block targets.`
        : `Close ${fmt(pending)} pending entries block-wise with daily targets.`
    );
  }
  if (criticalCount > 0) {
    recs.push(`Run school-level audit on ${fmt(criticalCount)} low-completion schools below 95%.`);
  }
  if ((overview.total_repeaters ?? 0) > 0) {
    recs.push(`Validate and clean up ${fmt(overview.total_repeaters ?? 0)} repeater records (${pct(overview.repeater_rate ?? 0, 2)}).`);
  }
  return recs.slice(0, 4);
};

export const buildDataEntryDefaultPriorityActions = (overview, criticalCount) => [
  {
    text: `Address critical schools (<95% completion)`,
    due: "Immediate",
    dueTone: "red",
  },
  {
    text: `Close pending entries (${fmt(overview?.pending_students ?? 0)} students)`,
    due: "7 Days",
    dueTone: "orange",
  },
  {
    text: "Drive certification for backlog schools",
    due: "14 Days",
    dueTone: "",
  },
  {
    text: "Validate repeater student records",
    due: "14 Days",
    dueTone: "green",
  },
];

export const buildDataEntryInsightTimeline = (overview, criticalCount, blockData) => {
  const pending = overview?.pending_students ?? 0;
  const nonCert = overview?.non_certified_schools ?? 0;
  const topPending = getHighestPendingBlockName(blockData);
  return [
    {
      step: 1,
      title: "Immediate Triage",
      text: `Address ${fmt(criticalCount)} critical schools & ${fmt(pending)} pending entries`,
      owner: "Block Officer",
    },
    {
      step: 2,
      title: "Block Escalation",
      text:
        topPending !== "—"
          ? `Close pending entries in ${titleCase(topPending)} and peer blocks`
          : "Close pending entries block-wise",
      owner: "Data Entry Admin",
    },
    {
      step: 3,
      title: "Certification Drive",
      text: `Push certification across ${fmt(nonCert)} non-certified schools`,
      owner: "School HM",
    },
    {
      step: 4,
      title: "Final Closure",
      text: `Audit ${fmt(overview?.total_repeaters ?? 0)} repeaters & certify ${fmt(overview?.certified_schools ?? 0)} schools`,
      owner: "District QA",
    },
  ];
};

export const buildDataEntryInsightsMarkdown = (overview, criticalCount, blockData) => {
  if (!overview) {
    return [
      "## Insights",
      "- No data entry status data available for the current filters.",
      "",
      "## Root Cause Signals",
      "- Missing or incomplete records in the selected scope.",
      "",
      "## Recommendations",
      "- Refresh data or broaden filters to include valid records.",
      "",
      "## Priority Action Items",
      "- Validate data import for data entry status.",
    ].join("\n");
  }

  return [
    "## Insights",
    `- Completion rate: **${pct(overview.completion_rate ?? 0, 2)}** across **${fmt(overview.total_students ?? 0)}** students.`,
    `- Pending students: **${fmt(overview.pending_students ?? 0)}** (${pct(overview.pending_rate ?? 0, 2)}).`,
    `- Certification rate: **${pct(overview.certification_rate ?? 0, 1)}** with **${fmt(overview.certified_schools ?? 0)}** schools certified.`,
    `- Critical schools (<95% completion): **${fmt(criticalCount)}**.`,
    `- Full completion schools: **${fmt(overview.full_completion_schools ?? 0)}** (${pct(overview.full_completion_rate ?? 0, 1)}).`,
    `- Repeater rate: **${pct(overview.repeater_rate ?? 0, 2)}** (${fmt(overview.total_repeaters ?? 0)} students).`,
    "",
    "## Root Cause Signals",
    ...buildDataEntryDefaultSignals(overview, criticalCount, blockData).map((s) => `- ${s}`),
    "",
    "## Recommendations",
    ...buildDataEntryDefaultRecommendations(overview, criticalCount, blockData).map((s) => `- ${s}`),
    "",
    "## Priority Action Items",
    ...buildDataEntryDefaultPriorityActions(overview, criticalCount).map((a) => `- ${a.text} (${a.due})`),
  ].join("\n");
};

export const buildQualityAuditClosurePlan = (lowCertBlocks) => {
  const names = lowCertBlocks
    .filter((b) => (b.certification_rate ?? 0) < 10)
    .slice(0, 3)
    .map((b) => titleCase(b.block_name))
    .join(", ");
  const escalation = names || "low-certification blocks";

  return [
    {
      phase: "Days 1–7",
      title: "Certification Triage",
      text: "Identify all full-entry, non-certified schools and allocate block-wise certification targets.",
    },
    {
      phase: "Days 8–15",
      title: "Repeater Validation",
      text: "Audit high repeater schools and confirm whether repeaters are genuine or duplicate entries.",
    },
    {
      phase: "Days 16–23",
      title: "Block Escalation",
      text: `Escalate low-certification blocks such as ${escalation} to district review.`,
    },
    {
      phase: "Days 24–30",
      title: "Final Sign-Off",
      text: "Freeze certified records, publish audit summary, and prepare exception report for leadership.",
    },
  ];
};
